import * as THREE from 'three';
import { seededRandom } from '../game/rng';
import { damp } from '../utils/math';
import { Templates as T } from './GeometryBuilder';

const RAIN_DROPS = 1600;
const RAIN_BOX = { x: 34, y: 26, z: 34 };

const RAINBOW_VERTEX = /* glsl */ `
  varying vec2 vUv;
  void main() {
    vUv = uv;
    gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
  }
`;

const RAINBOW_FRAGMENT = /* glsl */ `
  uniform float uOpacity;
  varying vec2 vUv;
  vec3 hue(float h) {
    return clamp(abs(mod(h * 6.0 + vec3(0.0, 4.0, 2.0), 6.0) - 3.0) - 1.0, 0.0, 1.0);
  }
  void main() {
    // uv.y wraps around the tube; use the outward-facing half as the band.
    float band = fract(vUv.y);
    float t = smoothstep(0.0, 1.0, band);
    vec3 col = hue(t * 0.8);
    float edge = smoothstep(0.0, 0.12, band) * smoothstep(1.0, 0.88, band);
    float ends = smoothstep(0.0, 0.08, vUv.x) * smoothstep(1.0, 0.92, vUv.x);
    gl_FragColor = vec4(col, uOpacity * edge * ends * 0.45);
    #include <colorspace_fragment>
  }
`;

/** Rain, drifting clouds and the post-rain rainbow. */
export class Weather {
  readonly group = new THREE.Group();
  /** Smoothed 0..1 rain amount (drives sky darkening too). */
  rain = 0;

  private readonly drops: THREE.LineSegments<THREE.BufferGeometry, THREE.LineBasicMaterial>;
  private readonly dropOffsets = new Float32Array(RAIN_DROPS * 3);
  private readonly clouds: THREE.Group[] = [];
  private readonly cloudMaterial: THREE.MeshLambertMaterial;
  private readonly rainbow: THREE.Mesh<THREE.TorusGeometry, THREE.ShaderMaterial>;
  private rainbowOpacity = 0;

  constructor() {
    const rand = seededRandom(1234);
    const positions = new Float32Array(RAIN_DROPS * 6);
    for (let i = 0; i < RAIN_DROPS; i++) {
      this.dropOffsets[i * 3] = (rand() - 0.5) * RAIN_BOX.x * 2;
      this.dropOffsets[i * 3 + 1] = rand() * RAIN_BOX.y;
      this.dropOffsets[i * 3 + 2] = (rand() - 0.5) * RAIN_BOX.z * 2;
    }
    const geometry = new THREE.BufferGeometry();
    geometry.setAttribute(
      'position',
      new THREE.BufferAttribute(positions, 3).setUsage(THREE.DynamicDrawUsage),
    );
    this.drops = new THREE.LineSegments(
      geometry,
      new THREE.LineBasicMaterial({
        color: '#d8e8ff',
        transparent: true,
        opacity: 0,
        depthWrite: false,
      }),
    );
    this.drops.frustumCulled = false;
    this.drops.visible = false;
    this.group.add(this.drops);

    this.cloudMaterial = new THREE.MeshLambertMaterial({
      color: '#ffffff',
      emissive: '#ffffff',
      emissiveIntensity: 0.35,
      flatShading: true,
    });
    for (let i = 0; i < 18; i++) {
      const cloud = new THREE.Group();
      const puffs = 4 + Math.floor(rand() * 4);
      for (let p = 0; p < puffs; p++) {
        const puff = new THREE.Mesh(T.ico1, this.cloudMaterial);
        const s = 6 + rand() * 7;
        puff.scale.set(s * 1.2, s * 0.8, s);
        puff.position.set((p - puffs / 2) * 7 + rand() * 4, rand() * 3, rand() * 6);
        cloud.add(puff);
      }
      const angle = rand() * Math.PI * 2;
      const r = 120 + rand() * 380;
      cloud.position.set(Math.cos(angle) * r, 80 + rand() * 60, Math.sin(angle) * r);
      cloud.userData.speed = 2 + rand() * 3;
      this.clouds.push(cloud);
      this.group.add(cloud);
    }

    this.rainbow = new THREE.Mesh(
      new THREE.TorusGeometry(320, 22, 12, 96, Math.PI),
      new THREE.ShaderMaterial({
        vertexShader: RAINBOW_VERTEX,
        fragmentShader: RAINBOW_FRAGMENT,
        uniforms: { uOpacity: { value: 0 } },
        transparent: true,
        depthWrite: false,
        fog: false,
        side: THREE.DoubleSide,
      }),
    );
    this.rainbow.position.set(40, -30, -620);
    this.rainbow.visible = false;
    this.group.add(this.rainbow);
  }

  update(
    dt: number,
    raining: boolean,
    rainbowLeft: number,
    night: number,
    focus: THREE.Vector3,
  ): void {
    this.rain += ((raining ? 1 : 0) - this.rain) * damp(0.8, dt);

    // Rain streaks follow the camera focus so they are always around the player.
    this.drops.visible = this.rain > 0.02;
    if (this.drops.visible) {
      const position = this.drops.geometry.getAttribute('position') as THREE.BufferAttribute;
      const array = position.array as Float32Array;
      for (let i = 0; i < RAIN_DROPS; i++) {
        let y = this.dropOffsets[i * 3 + 1]! - dt * 30;
        if (y < 0) y += RAIN_BOX.y;
        this.dropOffsets[i * 3 + 1] = y;
        const x = focus.x + this.dropOffsets[i * 3]!;
        const z = focus.z + this.dropOffsets[i * 3 + 2]!;
        array[i * 6] = x;
        array[i * 6 + 1] = y;
        array[i * 6 + 2] = z;
        array[i * 6 + 3] = x + 0.08;
        array[i * 6 + 4] = y + 0.7;
        array[i * 6 + 5] = z;
      }
      position.needsUpdate = true;
      this.drops.material.opacity = this.rain * 0.55;
    }

    const grey = 1 - this.rain * 0.45;
    this.cloudMaterial.color.setRGB(grey, grey, grey * 1.04);
    this.cloudMaterial.emissiveIntensity = (0.35 - this.rain * 0.25) * (1 - night * 0.8);
    for (const cloud of this.clouds) {
      cloud.position.x += (cloud.userData.speed as number) * dt * (1 + this.rain);
      if (cloud.position.x > 550) cloud.position.x = -550;
    }

    const target = rainbowLeft > 0 && night < 0.5 ? Math.min(1, rainbowLeft / 8) : 0;
    this.rainbowOpacity += (target - this.rainbowOpacity) * damp(1.5, dt);
    this.rainbow.visible = this.rainbowOpacity > 0.01;
    this.rainbow.material.uniforms.uOpacity!.value = this.rainbowOpacity;
  }
}
